import { createFileRoute, notFound } from '@tanstack/react-router';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslations } from 'use-intl';
import { isUuid } from '@leanin/shared';
import type { GroupId } from '@leanin/shared/ids';
import { useMember, memberPhotoUrl } from '@/lib/use-session';
import { useDebouncedValue } from '@/lib/use-debounced-value';
import { useFormatDate } from '@/lib/format-date.client';
import { NetworkDetailView } from '@/components/networks/NetworkDetailView';
import { groupDetailToNetworkVM } from '@/components/networks/adapters';
import { useEventAdapterContext } from '@/components/events/event-adapters.client';
import type { TabId } from '@/components/networks/NetworkDetailView';
import { isNotFoundApiError } from '@/lib/api-error';
import { NotFoundPage } from '@/components/shell/NotFoundPage';
import {
  groupKeys,
  useInfiniteGroupMembers,
  useInfiniteNetworkCircles,
} from '@/lib/queries/groups';
import { useInfiniteFeed } from '@/lib/queries/feed';

const VALID_TABS: ReadonlyArray<TabId> = [
  'overview',
  'feed',
  'members',
  'circles',
  'events',
  'resources',
];

function toTabId(v: unknown): TabId {
  if (typeof v === 'string' && (VALID_TABS as ReadonlyArray<string>).includes(v))
    return v as TabId;
  return 'overview';
}

type Search = { tab?: TabId };

export const Route = createFileRoute('/_public/networks/$id/')({
  validateSearch: (raw: Record<string, unknown>): Search => ({
    tab: toTabId(raw.tab),
  }),
  beforeLoad: ({ params }) => {
    if (!isUuid(params.id)) throw notFound();
  },
  component: NetworkDetailPage,
});

// Networks doesn't have a visitor/member split that requires a separate
// component — every role is handled by NetworkDetailView, which renders
// progressively from skeleton chrome (header + aside + tab bodies) to the
// fully-loaded view as each query lands. The role query is the only gate
// for leader-only fetches (`enabled: isLeader`); the rest stream in.
//
// Anonymous viewers (no session) get the same shape with `viewer` set to
// null and the feed / role / roster / join-requests queries disabled.
// The Feed and Members tabs are hidden by NetworkDetailView when viewer
// is null (member-only surfaces); Request-to-Join links to /auth/login
// with a `from=` return URL.
function NetworkDetailPage() {
  const { id } = Route.useParams();
  const me = useMember();
  const signedIn = me !== null;
  const t = useTranslations('Networks');
  const fmt = useFormatDate();
  const eventCtx = useEventAdapterContext();

  const detailQuery = useQuery(groupKeys.network.detail(id));
  const leadersQuery = useQuery(groupKeys.network.leaders(id));
  const circlesQuery = useInfiniteNetworkCircles(id, {
    includeArchived: false,
    pageSize: 24,
  });
  const eventsQuery = useQuery(
    groupKeys.network.events(id, { window: 'upcoming', pageSize: 4 }),
  );
  // Keyed under feedKeys so useCreateFeedPost/useReactToFeedPost/etc.
  // invalidations under feedKeys.lists() reach this query too — without
  // that, a post created via the Group composer never appears in this
  // detail page's feed tab until a hard reload. Disabled for anon — the
  // feed endpoint is member-only and the tab itself is hidden.
  const postsQuery = useInfiniteFeed(
    { scope: `group:${id}` },
    { enabled: signedIn },
  );
  const roleQuery = useQuery({
    ...groupKeys.viewerRole(id),
    enabled: signedIn,
  });

  const rawRole = roleQuery.data;
  const role: 'leader' | 'member' | null =
    rawRole === 'leader' ? 'leader' : rawRole === 'member' ? 'member' : null;
  const isLeader = role === 'leader';

  // Roster search: the displayed value updates immediately; the debounced
  // value drives the query key so the server only gets called 300ms after
  // the user stops typing — avoids a request-per-keystroke.
  const [rosterSearchInput, setRosterSearchInput] = useState('');
  const rosterSearchDebounced = useDebouncedValue(rosterSearchInput, 300);

  const rosterQuery = useInfiniteGroupMembers(id, {
    pageSize: 24,
    enabled: signedIn,
    search: rosterSearchDebounced || undefined,
  });
  const joinRequestsQuery = useQuery({
    ...groupKeys.network.joinRequests(id),
    enabled: isLeader,
  });

  // 404 → branded NotFoundPage directly (render-context `throw notFound()`
  // goes to TanStack's catch-boundary, not its 404 surface).
  if (isNotFoundApiError(detailQuery.error)) return <NotFoundPage />;
  // Other errors after the detail query has had a chance to fail — route
  // to the catch-boundary so the loading skeleton doesn't render forever.
  if (detailQuery.isError && !detailQuery.data) throw notFound();

  // Aggregate VM when the base slices (detail + leaders + circles + events)
  // are all in. Feed / roster / pending-join-requests stream independently
  // (passed alongside as their queries resolve).
  const detail = detailQuery.data ?? null;
  const leaders = leadersQuery.data?.items ?? null;
  const circles = circlesQuery.data?.pages.flatMap((p) => p.items) ?? null;
  const events = eventsQuery.data?.items ?? null;
  const feedItems = postsQuery.data?.pages.flatMap((p) => p.items) ?? null;
  const rosterItems = rosterQuery.data?.pages.flatMap((p) => p.items) ?? null;
  // fetchGroupJoinRequests already scopes to pending — no re-filter here.
  const pendingJoinRequests = isLeader
    ? (joinRequestsQuery.data?.items ?? [])
    : [];

  const network =
    detail !== null && leaders !== null && circles !== null && events !== null
      ? groupDetailToNetworkVM(
          detail,
          leaders,
          circles,
          events,
          role,
          {
            posts: feedItems ?? [],
            networkId: id as GroupId,
            rosterItems: rosterItems ?? [],
            pendingJoinRequests,
          },
          {
            eventCtx,
            leaderJoinedRole: (joinedAt) =>
              t('fallbacks.joinedRole', { date: fmt.medium(joinedAt) }),
          },
        )
      : null;

  const viewerPhoto = me ? memberPhotoUrl(me) : null;

  return (
    <NetworkDetailView
      networkId={id}
      network={network}
      viewerRole={role}
      viewer={
        me
          ? {
              id: me.id,
              name: `${me.firstName} ${me.lastName}`.trim(),
              photoUrl: viewerPhoto ?? null,
            }
          : null
      }
      feedItems={feedItems}
      postsHasMore={postsQuery.hasNextPage}
      onPostsLoadMore={() => void postsQuery.fetchNextPage()}
      isPostsLoadingMore={postsQuery.isFetchingNextPage}
      roster={network !== null ? network.roster : null}
      rosterHasMore={rosterQuery.hasNextPage}
      onRosterLoadMore={() => void rosterQuery.fetchNextPage()}
      circlesHasMore={circlesQuery.hasNextPage}
      onCirclesLoadMore={() => void circlesQuery.fetchNextPage()}
      isCirclesLoadingMore={circlesQuery.isFetchingNextPage}
      rosterSearch={rosterSearchInput}
      onRosterSearch={setRosterSearchInput}
    />
  );
}
